import { Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  useColumns,
} from "../../components/ui";
import { api, type BillSummary } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";

/**
 * What is still owed, and to whom.
 *
 * WHY THIS SCREEN EXISTS
 *
 * Collections had no list at all. An agent could only work from the bill
 * number on a copy the customer produced, so a bill nobody produced was a
 * bill nobody chased - which is the wrong way round for the one module whose
 * job is chasing money.
 *
 * NARROWER THAN THE SALES BILL LIST, ON PURPOSE
 *
 * That one is gated on SALES and carries the tax treatment and the taxable
 * value. This one answers the single question this module asks - who owes
 * what - and a settled bill drops off it entirely. Reading who owes money is
 * not a step towards raising or altering one, and a collection agent still
 * cannot do either.
 *
 * WHAT IS OWED IS DERIVED
 *
 * Summed from the payments themselves, in integer paise, by the server. Not a
 * column written when the bill was raised: somebody may have collected in the
 * field since, and asking a customer for money they have already paid is the
 * mistake this module most has to avoid.
 */
export function OutstandingBills() {
  const [bills, setBills] = useState<BillSummary[]>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function load() {
    try {
      setError(undefined);
      const { bills: loaded } = await api.outstandingBills();
      setBills(loaded);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load what is outstanding.",
      );
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const total = (bills ?? []).reduce(
    (sum, bill) => sum + Number(bill.outstandingAmount),
    0,
  );

  const columns = useColumns<BillSummary>(
    () => [
      {
        key: "Bill",
        header: "Bill",
        width: "170px",
        render: (bill) => (
          <Text fz={13} fw={600} className="num" truncate>
            {bill.reference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (bill) => (
          <Text fz={14} truncate>
            {bill.customerName}
          </Text>
        ),
      },
      {
        key: "Billed",
        header: "Billed",
        width: "130px",
        render: (bill) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(bill.billDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Text>
        ),
      },
      {
        key: "Bill total",
        header: "Bill total",
        width: "140px",
        align: "right",
        render: (bill) => (
          <Text fz={14} className="num" c="gray.7">
            {formatINR(bill.totalAmount)}
          </Text>
        ),
      },
      {
        key: "Collected",
        header: "Collected",
        width: "140px",
        align: "right",
        // Shown beside what is left, because "part paid" is the state an
        // agent most needs to see before knocking on the door.
        render: (bill) =>
          Number(bill.amountCollected) > 0 ? (
            <Text fz={14} className="num" c="teal.8">
              {formatINR(bill.amountCollected)}
            </Text>
          ) : (
            <Text fz={13} c="gray.6">
              nothing yet
            </Text>
          ),
      },
      {
        key: "Outstanding",
        header: "Outstanding",
        width: "150px",
        align: "right",
        render: (bill) => (
          <Text fz={15} fw={700} className="num">
            {formatINR(bill.outstandingAmount)}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <Page
      title="Outstanding"
      subtitle={
        bills
          ? `${bills.length} bill${bills.length === 1 ? "" : "s"} · ${formatINR(
              total.toFixed(2),
            )} to collect`
          : undefined
      }
      back="/collections"
      width="wide"
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!bills && !error && <LoadingSkeleton variant="list" count={5} />}

      {bills?.length === 0 && (
        <EmptyState
          title="Nothing is outstanding"
          description="Every bill has been paid in full."
        />
      )}

      {bills && bills.length > 0 && (
        <ListView
          rows={bills}
          columns={columns}
          rowKey={(bill) => bill.id}
          // Straight into the collection form for that bill, which is the
          // only thing anybody comes to this list to do.
          onOpen={(bill) =>
            navigate(`/collections/collect?reference=${encodeURIComponent(bill.reference)}`)
          }
          exportDataset="outstanding"
          caption="Outstanding bills"
          footnote="What is left is summed from the payments themselves, so a collection taken in the field is already reflected here."
          renderCard={(bill) => (
            <RecordCard
              key={bill.id}
              to={`/collections/collect?reference=${encodeURIComponent(bill.reference)}`}
              primary={bill.reference}
              secondary={bill.customerName}
              action={
                <span className="num" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {formatINR(bill.outstandingAmount)}
                </span>
              }
              meta={
                Number(bill.amountCollected) > 0
                  ? `${new Date(bill.billDate).toLocaleDateString()} · bill ${formatINR(
                      bill.totalAmount,
                    )}, ${formatINR(bill.amountCollected)} already collected`
                  : `${new Date(bill.billDate).toLocaleDateString()} · nothing collected yet`
              }
            />
          )}
        />
      )}
    </Page>
  );
}
